import React, { useState, useEffect } from "react";
import SummaryApi from "../../../common";

export default function SalaryManagement() {
  const [salaries, setSalaries] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [originalStatus, setOriginalStatus] = useState(""); // Track original status
  const [formData, setFormData] = useState({
    staffId: "",
    salaryamount: "",
    salaryAmountForThisMonth: "",
    unpaidLeaves: "",
    month: "",
    status: "Unpaid",
    paidOn: "",
    bankAccountId: "",
  });

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // ✅ Fetch staff list
  const fetchStaff = async () => {
    try {
      const res = await fetch(SummaryApi.staff.getAll.url);
      const data = await res.json();
      setStaffList(data);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  // ✅ Fetch bank accounts
  const fetchBankAccounts = async () => {
    try {
      const res = await fetch(SummaryApi.bankAccount.getAll.url);
      const data = await res.json();
      setBankAccounts(data.data || []);
    } catch (err) {
      console.error("Error fetching bank accounts:", err);
    }
  };

  // ✅ Fetch salary records
  const fetchSalaries = async () => {
    try {
      const res = await fetch(SummaryApi.getSalaries.url);
      const data = await res.json();
      setSalaries(data);
    } catch (err) {
      console.error("Error fetching salaries:", err);
    }
  };

  useEffect(() => {
    fetchSalaries();
    fetchStaff();
    fetchBankAccounts();
  }, []);

  // ✅ Helper function to get number of days in selected month (current year)
  const getDaysInMonth = (monthName) => {
    const monthIndex = months.indexOf(monthName);
    if (monthIndex === -1) return 30;
    const year = new Date().getFullYear();
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  // ✅ Auto-calculate salary for this month
  useEffect(() => {
    const { salaryamount, unpaidLeaves, month } = formData;
    if (!salaryamount || !month) return;

    const totalSalary = parseFloat(salaryamount);
    const leaves = parseInt(unpaidLeaves || 0, 10);
    const daysInMonth = getDaysInMonth(month);

    const perDaySalary = totalSalary / daysInMonth;
    const salaryForThisMonth = totalSalary - perDaySalary * leaves;

    setFormData((prev) => ({
      ...prev,
      salaryAmountForThisMonth: salaryForThisMonth.toFixed(2),
    }));
  }, [formData.salaryamount, formData.unpaidLeaves, formData.month]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const resetForm = () => {
    setFormData({
      staffId: "",
      salaryamount: "",
      salaryAmountForThisMonth: "",
      unpaidLeaves: "",
      month: "",
      status: "Unpaid",
      paidOn: "",
      bankAccountId: "",
    });
    setIsEditing(false);
    setEditId(null);
    setOriginalStatus("");
  };

  // ✅ Create or Update Salary Record
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate bank account selection if status is Paid
    if (formData.status === "Paid" && !formData.bankAccountId) {
      alert("Please select a bank account for payment");
      return;
    }

    const url = isEditing
      ? SummaryApi.updateSalary(editId).url
      : SummaryApi.createSalary.url;

    const method = isEditing ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      
      if (!res.ok) {
        alert(data.message || "Error processing salary");
        return;
      }

      alert(data.message || "Salary processed successfully");
      fetchSalaries();
      fetchBankAccounts(); // Refresh bank accounts to show updated balance
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error("Error submitting salary:", err);
      alert("Error processing salary");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await fetch(SummaryApi.deleteSalary(id).url, { method: "DELETE" });
      fetchSalaries();
    } catch (err) {
      console.error("Error deleting salary:", err);
    }
  };

  const handleEdit = (salary) => {
    setIsEditing(true);
    setEditId(salary._id);
    setOriginalStatus(salary.status); // Store the original status
    setFormData({
      staffId: salary.staffId?._id || "",
      salaryamount: salary.salaryamount || "",
      salaryAmountForThisMonth: salary.salaryAmountForThisMonth || "",
      unpaidLeaves: salary.unpaidLeaves || "",
      month: salary.month || "",
      status: salary.status || "Unpaid",
      paidOn: salary.paidOn ? salary.paidOn.split("T")[0] : "",
      bankAccountId: salary.bankAccountId?._id || "",
    });
    setShowModal(true);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">Salary Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md"
        >
          Add Salary
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {isEditing ? "Edit Salary Record" : "Add Salary Record"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ✅ Show staff dropdown only when adding */}
              {!isEditing && (
                <div>
                  <label className="block text-gray-700">Select Staff</label>
                  <select
                    name="staffId"
                    value={formData.staffId}
                    onChange={handleChange}
                    className="border rounded-md w-full px-3 py-2"
                    required
                  >
                    <option value="">-- Select Staff --</option>
                    {staffList.map((staff) => (
                      <option key={staff._id} value={staff._id}>
                        {staff.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-gray-700">Total Salary Amount</label>
                <input
                  type="number"
                  name="salaryamount"
                  value={formData.salaryamount}
                  onChange={handleChange}
                  className="border rounded-md w-full px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700">Unpaid Leaves</label>
                <input
                  type="number"
                  name="unpaidLeaves"
                  value={formData.unpaidLeaves}
                  onChange={handleChange}
                  className="border rounded-md w-full px-3 py-2"
                />
              </div>

              {/* Month Dropdown */}
              <div>
                <label className="block text-gray-700">Select Month</label>
                <select
                  name="month"
                  value={formData.month}
                  onChange={handleChange}
                  className="border rounded-md w-full px-3 py-2"
                  required
                >
                  <option value="">-- Select Month --</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* ✅ Auto-calculated salary for this month */}
              <div>
                <label className="block text-gray-700">Salary for This Month</label>
                <input
                  type="number"
                  name="salaryAmountForThisMonth"
                  value={formData.salaryAmountForThisMonth}
                  readOnly
                  className="border rounded-md w-full px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-gray-700">Status</label>
                {isEditing && originalStatus === "Paid" ? (
                  // If editing and already paid, show as read-only
                  <div className="relative">
                    <input
                      type="text"
                      value="Paid"
                      readOnly
                      className="border rounded-md w-full px-3 py-2 bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ⚠️ Paid status cannot be changed to Unpaid
                    </p>
                  </div>
                ) : (
                  // If adding new or editing unpaid, allow selection
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="border rounded-md w-full px-3 py-2"
                    required
                  >
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                  </select>
                )}
              </div>

              {/* Bank Account Selection - Show only when status is Paid */}
              {formData.status === "Paid" && (
                <>
                  <div>
                    <label className="block text-gray-700">
                      Select Bank Account <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="bankAccountId"
                      value={formData.bankAccountId}
                      onChange={handleChange}
                      className="border rounded-md w-full px-3 py-2"
                      required
                      disabled={isEditing && originalStatus === "Paid"} // Disable if already paid
                    >
                      <option value="">-- Select Bank Account --</option>
                      {bankAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountName} - {account.accountNumber} (Balance: ₹{account.balance})
                        </option>
                      ))}
                    </select>
                    {isEditing && originalStatus === "Paid" && (
                      <p className="text-xs text-gray-500 mt-1">
                        Bank account cannot be changed for paid salaries
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700">Paid On</label>
                    <input
                      type="date"
                      name="paidOn"
                      value={formData.paidOn}
                      onChange={handleChange}
                      className="border rounded-md w-full px-3 py-2"
                      disabled={isEditing && originalStatus === "Paid"} // Disable if already paid
                    />
                  </div>
                </>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="bg-gray-300 px-4 py-2 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md"
                >
                  {isEditing ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Salary Table */}
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-300 mt-4 text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">Staff Name</th>
              <th className="p-2 border">Month</th>
              <th className="p-2 border">Total Salary</th>
              <th className="p-2 border">This Month</th>
              <th className="p-2 border">Unpaid Leaves</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Bank Account</th>
              <th className="p-2 border">Paid On</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {salaries.map((item) => (
              <tr key={item._id} className="text-center border-t">
                <td className="p-2 border">{item.staffId?.name || "N/A"}</td>
                <td className="p-2 border">{item.month}</td>
                <td className="p-2 border">₹{item.salaryamount}</td>
                <td className="p-2 border">₹{item.salaryAmountForThisMonth}</td>
                <td className="p-2 border">{item.unpaidLeaves}</td>
                <td className="p-2 border">
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.status === "Paid" 
                      ? "bg-green-100 text-green-800" 
                      : "bg-yellow-100 text-yellow-800"
                  }`}>
                    {item.status}
                  </span>
                </td>
                <td className="p-2 border">
                  {item.bankAccountId 
                    ? `${item.bankAccountId.accountName} (${item.bankAccountId.accountNumber})`
                    : "-"
                  }
                </td>
                <td className="p-2 border">
                  {item.paidOn
                    ? new Date(item.paidOn).toLocaleDateString("en-GB")
                    : "-"}
                </td>
                <td className="p-2 border flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="bg-yellow-500 text-white px-2 py-1 rounded-md text-xs"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="bg-red-500 text-white px-2 py-1 rounded-md text-xs"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}